import { useContext, useMemo, useState } from 'react';
import { shallowEqual } from 'react-redux';

import { Box, Paragraph } from '@zillow/constellation-10';
import { LoadingButton } from '@zg-rentals/loading-components';
import { CreateNewTaskModal, NoTasksEmptyState, TasksTable } from '@zg-rentals/maintenance-utils';
import { useRentalManagerUserPropertySummaryWebApiHandler_maintenance } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { RMWContext } from '../../providers/RMWContext';

import { useSelector } from '../../selectors/useSelector';

import propertyUtils from '../../utils/propertyUtils';

import TimeConstants from '../../constants/TimeConstants';

import ConnectedModuleCard from '../ConnectedModuleCard';

const ConnectedMaintenanceModule = () => {
  const { paths } = useContext(RMWContext);
  const { listingAlias, street, city, state, zip, unit, name } = useSelector((state) => {
    return {
      listingAlias: state.properties.currentProperty.listingAlias,
      street: state.properties.currentProperty.street,
      city: state.properties.currentProperty.city,
      state: state.properties.currentProperty.state,
      zip: state.properties.currentProperty.zip,
      unit: state.properties.currentProperty.unit,
      name: state.properties.currentProperty.name,
    };
  }, shallowEqual);

  const formattedCompleteAddress = useMemo(
    () =>
      propertyUtils.formatAddress({
        street,
        city,
        state,
        zip,
        unit,
        name,
      }).formattedCompleteAddress,
    [street, city, state, zip, unit, name],
  );

  const [displayCreateNewTaskModal, setDisplayCreateNewTaskModal] = useState(false);

  // use isLoading condition in order apply conditional logic on the initial request
  const { data, isLoading, error } = useRentalManagerUserPropertySummaryWebApiHandler_maintenance(
    { listingAlias },
    { enabled: Boolean(listingAlias), staleTime: TimeConstants.SECONDS_10 },
  );

  if (error) {
    return <Paragraph>Error occurred.</Paragraph>;
  }

  const handleRowClick = (taskId: string) => {
    window.router.updateLocation(`/rental-manager/maintenance-tasks/task?id=${taskId}`);
  };
  const hasMaintenanceTasks = (data?.maintenanceTasks?.length ?? 0) > 0;
  return (
    <div data-testid="ConnectedMaintenanceModule">
      {hasMaintenanceTasks || isLoading ? (
        <ConnectedModuleCard
          title="Maintenance"
          ctaComponent={
            <LoadingButton
              tone="brand"
              emphasis="filled"
              onClick={() => setDisplayCreateNewTaskModal(true)}
              isLoading={isLoading}
              loadingWidth="10rem"
            >
              Create new task
            </LoadingButton>
          }
        >
          <TasksTable
            formattedCompleteAddress={formattedCompleteAddress}
            fromExternalApp={true}
            showAddressColumn={false}
            tasksToDisplay={data?.maintenanceTasks}
            onRowClick={handleRowClick}
            isLoading={isLoading}
          />
          <Box css={{ display: 'flex', justifyContent: 'center' }}>
            <LoadingButton
              asChild={true}
              fluid={false}
              isLoading={isLoading}
              loadingWidth="50%"
              css={{ marginTop: '300' }}
            >
              <a href={paths.maintenanceTrackerPage}>View tasks for all properties</a>
            </LoadingButton>
          </Box>
        </ConnectedModuleCard>
      ) : (
        <NoTasksEmptyState setDisplayCreateNewTaskModal={setDisplayCreateNewTaskModal} />
      )}

      {displayCreateNewTaskModal && (
        <CreateNewTaskModal
          listingAlias={listingAlias}
          onClose={(newTaskId?: string) => {
            if (newTaskId) {
              window.location.href = `${paths.maintenanceTrackerPage}/task?id=${newTaskId}`;
            }
            setDisplayCreateNewTaskModal(false);
          }}
        />
      )}
    </div>
  );
};

export default ConnectedMaintenanceModule;
